import React, { useState, useEffect } from "react";
import { Card, Input } from "@axin-org/comet";
import { initRsqlCriteria, GSBuilder } from "utils/query.utils";
import { Operators, RSQLFilterExpression } from "rsql-criteria-typescript";
import { useTranslation } from "react-i18next";
import { Fa } from "composants/Icon";
import { Button } from "composants/button";

import { Pojo } from "types/Galaxy";
import AutoCompleteAndLabel from "composants/autocomplete/AutoCompleteAndLabel";
import { createMany, find, deleteMany } from "api/entities";
import Modal from "composants/Modal/Modal";
import toaster from "composants/toaster/toaster";

interface FieldSelectorProps {
  table: any;
  selectedFields: string[];
  onClose: () => void;
  onFieldsSelected: (fields: string[]) => void;
  syjPanelTable: Pojo | null;
}

const FieldSelector: React.FC<FieldSelectorProps> = ({
  table,
  onClose,
  onFieldsSelected,
  syjPanelTable
}) => {
  const { t } = useTranslation();
  const [panelColumns, setPanelColumns] = useState<Pojo[]>([]);
  const [originalColumns, setOriginalColumns] = useState<Pojo[]>([]);
  const [selectedLabels, setSelectedLabels] = useState<Record<string, string>>({});
  const [currentField, setCurrentField] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (table) {
      const rsql = initRsqlCriteria();
      rsql.filters.and(new RSQLFilterExpression("syjPanelId.id", Operators.Equal, table.panelId));
      find("syjPanelEditorColumn", rsql.build(), 0, 1000, true).then((res: any) => {
        if (res.data && res.data.data.length > 0) {
          // Trier par position pour afficher dans l'ordre correct
          const sortedColumns = res.data.data.sort(
            (a: any, b: any) => (a.position || 0) - (b.position || 0)
          );
          setPanelColumns(sortedColumns);
          setOriginalColumns(sortedColumns);
          const labelMap: Record<string, string> = {};
          sortedColumns.forEach((col: any) => {
            const label =
              col.syjColumnId?.labelDetails ||
              col.syjColumnId?.label ||
              col.labelDetails ||
              col.label ||
              col.id;

            labelMap[col.id] = label;
          });

          setSelectedLabels(labelMap);
        }
      });
    }
  }, [table]);

  const handleAddField = (item: any) => {
    if (item && !panelColumns.some(col => col.syjColumnId?.id === item.id)) {
      const tempId = `temp_${Date.now()}_${item.id}`;
      const newPosition =
        panelColumns.length > 0 ? Math.max(...panelColumns.map(col => col.position ?? 0)) + 1 : 0;
      const newColumn: Pojo = {
        id: tempId,
        uuid: tempId,
        tableName: "syjPanelEditorColumn",
        label: item.label || "",
        labelDetails: item.labelDetails || "",
        syjPanelId: { id: table.panelId },
        syjColumnId: item,
        position: newPosition,
        etat: "ACTIF",
        version: 0
      };
      setPanelColumns(prev => [...prev, newColumn]);
      setSelectedLabels(prev => ({
        ...prev,
        [newColumn.id]: item.labelDetails || item.label || item.id
      }));
      setCurrentField(null);
    }
  };

  const handleRemoveField = (column: Pojo) => {
    setPanelColumns(prev => {
      const filtered = prev.filter(col => col.id !== column.id);
      // Réorganiser les positions après suppression
      return filtered.map((col, index) => ({
        ...col,
        position: index
      }));
    });
  };

  const handlePositionChange = (columnId: string, newPosition: number | string) => {
    setPanelColumns(prev =>
      prev.map(col => (col.id === columnId ? { ...col, position: newPosition } : col))
    );
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      // Supprimer toutes les colonnes existantes
      const existingColumnIds = originalColumns.map(col => col.id);
      if (existingColumnIds.length > 0) {
        await deleteMany("syjPanelEditorColumn", existingColumnIds, "ADMIN_GALAXY");
      }

      // Recréer toutes les colonnes avec les nouvelles positions
      if (panelColumns.length > 0) {
        const allColumnsToCreate = panelColumns.map((col, index) => {
          const position =
            typeof col.position === "number" ? col.position : col.position === "" ? 0 : index;
          return {
            tableName: "syjPanelEditorColumn",
            syjPanelId: table.panelId,
            syjColumnId: col.syjColumnId?.id,
            position: position,
            etat: "ACTIF"
          };
        });
        await createMany("syjPanelEditorColumn", allColumnsToCreate, "ADMIN_GALAXY");
      }

      const finalColumnIds = panelColumns
        .map(col => col.syjColumnId?.id)
        .filter(id => id && typeof id === "string" && !id.toString().startsWith("temp_"));

      onFieldsSelected(finalColumnIds);
      toaster.success(t("commun_modification_enregistrees"));

      setIsSaving(false);
      onClose();
    } catch (error) {
      console.error("Erreur lors de l'enregistrement des colonnes:", error);
      setIsSaving(false);
    }
  };

  return (
    <Modal
      onValidate={handleSave}
      onClose={onClose}
      title={t("commun_selection_colonnes")}
      disableFooterButton={isSaving}
    >
      <Card>
        <div className="p-6 space-y-4">
          <div className="mb-6">
            <AutoCompleteAndLabel
              id="field-selector"
              sjmoCode="ADMIN_GALAXY"
              joinTableName="syjColumn"
              joinListFields="sjcoColumn,labelDetails"
              label={t("commun_ajouter_colonne")}
              value={currentField}
              onItemChange={handleAddField}
              additionalClause={GSBuilder.AND(
                GSBuilder.Comparison("syjTablesId.id", "OPER_EQ", syjPanelTable?.syjTablesId),
                GSBuilder.Comparison("sjcoColumn", "OPER_LIKE_ANYWHERE", "")
              )}
            />
          </div>

          {panelColumns.length === 0 ? (
            <div className="text-gray-500 italic">{t("commun_aucun_champ_selectionne")}</div>
          ) : (
            <div className="space-y-2">
              {panelColumns.map((column, index) => (
                <div key={column.id} className="flex items-center justify-between p-6">
                  <span>
                    {selectedLabels[column.id] ||
                      column.syjColumnId?.labelDetails ||
                      column.syjColumnId?.label ||
                      column.labelDetails ||
                      column.label ||
                      column.id}
                  </span>
                  <div className="flex items-center justify-center space-x-6">
                    <Input
                      type="number"
                      value={column.position ?? index}
                      onChange={e => {
                        const value = e.target.value;
                        if (value === "") {
                          handlePositionChange(column.id, "");
                        } else {
                          handlePositionChange(column.id, parseInt(value) || 0);
                        }
                      }}
                      style={{ width: "50px", textAlign: "center", marginRight: "20px" }}
                    />
                    <Button
                      color="danger"
                      onClick={() => handleRemoveField(column)}
                      size="small"
                      className="is-light"
                    >
                      <Fa icon="trash" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4">
            <p className="text-sm text-gray-500">{panelColumns.length} champs sélectionnés</p>
          </div>
        </div>
      </Card>
    </Modal>
  );
};

export default FieldSelector;
